import React from "react";

import { format } from "date-fns";
import { CalendarIcon, Clock } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";

interface DeliveryDateSelectorProps {
  deliveryDate: Date | undefined;
  requestedDeliveryDateStart: Date | undefined;
  requestedDeliveryDateEnd: Date | undefined;
  setDeliveryDate: (date: Date | undefined) => void;
  setRequestedDeliveryDateStart: (date: Date | undefined) => void;
  setRequestedDeliveryDateEnd: (date: Date | undefined) => void;
}

const DeliveryDateSelector: React.FC<DeliveryDateSelectorProps> = ({
  deliveryDate,
  requestedDeliveryDateStart,
  requestedDeliveryDateEnd,
  setDeliveryDate,
  setRequestedDeliveryDateStart,
  setRequestedDeliveryDateEnd,
}) => {
  // Function to handle start time change
  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!deliveryDate) return;
    const [hours, minutes] = e.target.value.split(":").map(Number);
    const newDate = new Date(deliveryDate);
    newDate.setHours(hours, minutes);
    setRequestedDeliveryDateStart(newDate);
  };

  // Function to handle end time change
  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!deliveryDate) return;
    const [hours, minutes] = e.target.value.split(":").map(Number);
    const newDate = new Date(deliveryDate);
    newDate.setHours(hours, minutes);
    setRequestedDeliveryDateEnd(newDate);
  };

  // When date changes, update both start and end dates
  const handleDateChange = (date: Date | undefined) => {
    setDeliveryDate(date);

    if (date) {
      // If we already have start/end times, preserve them
      if (requestedDeliveryDateStart) {
        const newStartDate = new Date(date);
        newStartDate.setHours(
          requestedDeliveryDateStart.getHours(),
          requestedDeliveryDateStart.getMinutes()
        );
        setRequestedDeliveryDateStart(newStartDate);
      } else {
        // Default to 9:00 AM
        const newStartDate = new Date(date);
        newStartDate.setHours(9, 0);
        setRequestedDeliveryDateStart(newStartDate);
      }

      if (requestedDeliveryDateEnd) {
        const newEndDate = new Date(date);
        newEndDate.setHours(
          requestedDeliveryDateEnd.getHours(),
          requestedDeliveryDateEnd.getMinutes()
        );
        setRequestedDeliveryDateEnd(newEndDate);
      } else {
        // Default to 6:00 PM
        const newEndDate = new Date(date);
        newEndDate.setHours(18, 0);
        setRequestedDeliveryDateEnd(newEndDate);
      }
    } else {
      setRequestedDeliveryDateStart(undefined);
      setRequestedDeliveryDateEnd(undefined);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="delivery-date">Fecha de entrega solicitada</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="delivery-date"
              variant="outline"
              className={`mt-2 w-full justify-start text-left font-normal ${
                !deliveryDate && "text-muted-foreground"
              }`}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {deliveryDate ? (
                format(deliveryDate, "PPP")
              ) : (
                <span>Seleccionar fecha</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={deliveryDate}
              onSelect={handleDateChange}
              initialFocus
              disabled={(date) => date < new Date()}
            />
          </PopoverContent>
        </Popover>
      </div>

      {deliveryDate && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="start-time">Hora inicio</Label>
            <div className="mt-2 flex items-center">
              <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
              <Input
                id="start-time"
                type="time"
                value={
                  requestedDeliveryDateStart
                    ? format(requestedDeliveryDateStart, "HH:mm")
                    : "09:00"
                }
                onChange={handleStartTimeChange}
                className="w-full"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="end-time">Hora fin</Label>
            <div className="mt-2 flex items-center">
              <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
              <Input
                id="end-time"
                type="time"
                value={
                  requestedDeliveryDateEnd
                    ? format(requestedDeliveryDateEnd, "HH:mm")
                    : "18:00"
                }
                onChange={handleEndTimeChange}
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryDateSelector;
